import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getMe, logoutSession } from "~/services/auth";
import { navigateTo } from "~/utils/navigation";
import { normalizePermissions } from "./permissions";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: "loading", me: null, error: null });
  // silent: recarrega o auth/me sem voltar para "loading" (não troca a página pelo spinner nem a desmonta).
  const [attempt, setAttempt] = useState({ n: 0, silent: false });

  useEffect(() => {
    let alive = true;
    if (!attempt.silent) setState({ status: "loading", me: null, error: null });
    getMe().then(
      (me) => {
        if (alive) setState({ status: "authenticated", me, error: null });
      },
      (error) => {
        if (!alive) return;
        if (error?.response?.status === 401) setState({ status: "anonymous", me: null, error: null });
        // No silencioso, outros erros mantêm o estado atual (a tela segue com o me que já tinha).
        else if (!attempt.silent) setState({ status: "error", me: null, error });
      }
    );
    return () => {
      alive = false;
    };
  }, [attempt]);

  // reload() = recarga com spinner (ex.: "Tentar de novo"); reload({ silent: true }) = recarga em segundo plano.
  // Aceita ser usado direto como onClick (o evento não tem `silent`).
  const reload = useCallback((options) => setAttempt((a) => ({ n: a.n + 1, silent: options?.silent === true })), []);

  const logout = useCallback(async () => {
    try {
      await logoutSession();
    } catch (error) {
      if (error?.response?.status !== 401) throw error;
    }
    navigateTo("/login");
  }, []);

  const value = useMemo(
    () => ({ ...state, permissions: normalizePermissions(state.me?.permissions), logout, reload }),
    [state, logout, reload]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return context;
}
